/**
 * 86bc1ymu6: the single source of truth for the self credit check's steps.
 *
 * Both the progress rail (ReportLayout) and the overview cards (Dashboard)
 * build from this, so they can never disagree about what is done, what is
 * reachable, or what comes next.
 *
 * Four steps, in the order Rich asked for on the tickets: accept the terms,
 * pay, verify with Experian, then generate the report — so the user knows
 * what they are paying for before they are asked to pay (86bc1ymu6), and
 * verification is part of the flow rather than a wall in front of it
 * (86bc63aqb).
 *
 * There is deliberately no personal-information step. Verification is the
 * /rentrisk-verification check, which keeps its own attempt limits and
 * lockout, and the report pull is bound to the consumer token that produces —
 * not to any form fields. A user whose verification still stands finds step 3
 * already complete. 86bcfa7n0: that check runs inside these steps
 * (ReportVerificationController), not on its own page.
 *
 * `available` mirrors ReportController's own guards rather than simply
 * "previous step is done", so the UI never offers a link that would just
 * bounce the user somewhere else.
 *
 * 86bc8uyd2: a renter who bought the Applicant Boost also gets its four Plaid
 * verifications, last and optional. They are open as soon as the report is
 * paid, stay revisitable for their downloads, are never the "next" step and
 * do not count toward progress — skipping them still leaves the check 100%.
 *
 * 86bcahgev: a free report (a renter buying a home) is paid from the start,
 * so its payment step shows as done, tagged Free.
 *
 * 86bce6r42: a report started from then on (`pays_to_unlock`) is paid for
 * after Experian authentication, in three steps: terms, authenticate, then
 * unlock the report on the "your custom report is ready" page — paying opens
 * the report. Older reports keep the four steps above.
 */
const BOOST_STEPS = [
    {
        key: 'boost_identity',
        name: 'Identity Verification',
        description: 'Verify your identity with Plaid so a landlord can trust it is really you.',
        icon: 'id-card',
        // Runs from the dashboard itself, so the rail links back there.
        routeName: 'reports.dashboard',
        resultType: 'idv',
    },
    {
        key: 'boost_income',
        name: 'Income Verification',
        description: 'Connect your bank so we can verify your income.',
        icon: 'dollar',
        routeName: 'reports.boost.income',
        resultType: 'bank_income',
    },
    {
        key: 'boost_employment',
        name: 'Employment Verification',
        description: 'Connect your payroll account to verify your job and income details.',
        icon: 'building',
        routeName: 'reports.boost.employment',
        resultType: 'payroll',
    },
    {
        key: 'boost_assets',
        name: 'Asset Verification',
        description: 'Connect your bank accounts to verify your assets.',
        icon: 'landmark',
        routeName: 'reports.boost.assets',
        resultType: 'assets',
    },
]

/**
 * Which Boost verifications the report has, keyed by result type.
 */
export function boostResults(report) {
    const types = new Set((report.boost_verifications || []).map((verification) => verification.type))

    return Object.fromEntries(BOOST_STEPS.map((step) => [step.resultType, types.has(step.resultType)]))
}

export function buildReportSteps(report, { identityVerified = false } = {}) {
    const id = report.id
    const paid = !!report.paid
    const free = !!report.is_free
    const termsAccepted = !!report.terms_and_conditions
    const verified = !!identityVerified
    const unlockFirst = !!report.pays_to_unlock

    if (unlockFirst) {
        return finalize(report, unlockSteps(report, { id, paid, termsAccepted, verified }))
    }

    const steps = [
        {
            key: 'terms_and_conditions',
            number: 1,
            name: 'Terms and Conditions',
            description: 'Please read and accept the terms and conditions before proceeding.',
            icon: 'file-contract',
            href: route('reports.terms-and-conditions', id),
            complete: termsAccepted,
            available: true,
            lockedHint: '',
        },
        {
            key: 'payment',
            number: 2,
            name: 'Payment',
            description: free ? 'This report is free. No payment needed.' : 'Purchase the report to proceed.',
            icon: 'credit-card',
            href: route('reports.billing', id),
            complete: paid,
            free,
            // Not offered once paid: billingReport() redirects a paid report
            // away rather than showing a card form for a succeeded intent.
            available: termsAccepted && !paid,
            lockedHint: 'Accept the Terms and Conditions first',
        },
        {
            key: 'verify_identity',
            number: 3,
            name: 'Experian Authentication',
            description: 'Verify your identity with Experian so we can pull your report.',
            icon: 'id-card',
            href: route('reports.verify-identity', id),
            complete: verified,
            available: termsAccepted && paid && !verified,
            lockedHint: 'Complete Payment first',
        },
        {
            key: 'credit_background_report',
            number: 4,
            name: 'Background Check Report',
            description: 'Review your background check report.',
            icon: 'file-magnifying-glass',
            href: route('reports.credit-report', id),
            complete: !!report.report_id,
            available: termsAccepted && paid && verified,
            lockedHint: termsAccepted && paid ? 'Complete Experian Authentication first' : 'Complete Payment first',
            // Generating the report is not the end of it — the whole point is
            // to be able to open it again afterwards.
            revisitable: true,
        },
    ]

    return finalize(report, steps)
}

/**
 * 86bce6r42: terms → Experian Authentication → Unlock Report. Rich (ClickUp,
 * 2026-10-07): three steps, and paying goes straight to the report — so once
 * paid, Unlock Report is done and opens the report, which is also how a pull
 * that failed after payment is retried. Mirrors ReportController::dashboard()/
 * unlock() and creditReport().
 */
function unlockSteps(report, { id, paid, termsAccepted, verified }) {
    return [
        {
            key: 'terms_and_conditions',
            number: 1,
            name: 'Terms and Conditions',
            description: 'Please read and accept the terms and conditions before proceeding.',
            icon: 'file-contract',
            href: route('reports.terms-and-conditions', id),
            complete: termsAccepted,
            available: true,
            lockedHint: '',
        },
        {
            key: 'verify_identity',
            number: 2,
            name: 'Experian Authentication',
            description: 'Verify your identity with Experian so we can build your report.',
            icon: 'id-card',
            href: route('reports.verify-identity', id),
            complete: verified,
            available: termsAccepted && !verified,
            lockedHint: 'Accept the Terms and Conditions first',
        },
        {
            key: 'payment',
            number: 3,
            name: 'Unlock Report',
            description: paid ? 'Your report is unlocked. Open it any time.' : 'Your custom report is ready. Unlock it to view it.',
            icon: paid ? 'file-magnifying-glass' : 'credit-card',
            href: route(paid ? 'reports.credit-report' : 'reports.unlock', id),
            complete: paid,
            available: termsAccepted && verified,
            lockedHint: termsAccepted ? 'Complete Experian Authentication first' : 'Accept the Terms and Conditions first',
            revisitable: paid,
        },
    ]
}

function finalize(report, steps) {
    const id = report.id
    const paid = !!report.paid

    if (report.applicant_boost_paid_at) {
        const results = boostResults(report)

        BOOST_STEPS.forEach((boostStep) => {
            steps.push({
                key: boostStep.key,
                number: steps.length + 1,
                name: boostStep.name,
                description: boostStep.description,
                icon: boostStep.icon,
                href: route(boostStep.routeName, id),
                complete: results[boostStep.resultType],
                available: paid,
                lockedHint: 'Complete Payment first',
                revisitable: true,
                optional: true,
            })
        })
    }

    // The next step is the first one the user can actually act on.
    const next = steps.find((step) => !step.optional && !step.complete && step.available)

    return steps.map((step) => ({
        ...step,
        locked: !step.complete && !step.available,
        isNext: step === next,
    }))
}

export function reportProgress(steps) {
    const required = steps.filter((step) => !step.optional)
    const completed = required.filter((step) => step.complete).length

    return {
        completed,
        total: required.length,
        percentage: Math.round((completed / required.length) * 100),
    }
}
